import { useState, useEffect } from "react";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";

// ── Estilos compartidos del modal (mismo que InvoiceActionsModal) ────────
const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)",
  zIndex: 1000,
};
const MODAL = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  background: "#1a2535",
  border: "1px solid #2d3b4e",
  borderRadius: 12,
  padding: "24px",
  width: "min(480px,93vw)",
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};
const LABEL_STYLE = {
  display: "block",
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "#9ca3af",
  marginBottom: 5,
};
const INPUT_STYLE = {
  width: "100%",
  background: "#111827",
  border: "1px solid #374151",
  borderRadius: 8,
  padding: "9px 12px",
  color: "#f9fafb",
  fontSize: "0.9rem",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

function ModalHeader({
  icon,
  title,
  subtitle,
  onClose,
  iconColor = "#f97316",
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            background: `${iconColor}22`,
            border: `1px solid ${iconColor}44`,
            borderRadius: 9,
            padding: "9px 11px",
            color: iconColor,
          }}
        >
          <i className={`fa-solid ${icon}`} style={{ fontSize: "1.1rem" }} />
        </div>
        <div>
          <h3
            style={{
              margin: 0,
              color: "#f1f5f9",
              fontSize: "1rem",
              fontWeight: 700,
            }}
          >
            {title}
          </h3>
          {subtitle && (
            <p
              style={{
                margin: "2px 0 0",
                color: "#64748b",
                fontSize: "0.77rem",
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          color: "#4b5e75",
          cursor: "pointer",
          fontSize: "1.1rem",
        }}
        onMouseOver={(e) => (e.currentTarget.style.color = "#9ca3af")}
        onMouseOut={(e) => (e.currentTarget.style.color = "#4b5e75")}
      >
        ✕
      </button>
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: "1px solid #2d3b4e", margin: "16px 0" }} />;
}

function AlertBox({ type, children }) {
  const map = {
    warning: {
      bg: "rgba(251,191,36,0.08)",
      border: "rgba(251,191,36,0.3)",
      left: "#fbbf24",
      color: "#fde68a",
    },
    danger: {
      bg: "rgba(239,68,68,0.08)",
      border: "rgba(239,68,68,0.3)",
      left: "#ef4444",
      color: "#fca5a5",
    },
    info: {
      bg: "rgba(96,165,250,0.08)",
      border: "rgba(96,165,250,0.3)",
      left: "#60a5fa",
      color: "#bfdbfe",
    },
    success: {
      bg: "rgba(74,222,128,0.08)",
      border: "rgba(74,222,128,0.3)",
      left: "#4ade80",
      color: "#bbf7d0",
    },
  };
  const s = map[type] ?? map.info;
  return (
    <div
      style={{
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderLeft: `3px solid ${s.left}`,
        borderRadius: 6,
        padding: "10px 14px",
        color: s.color,
        fontSize: "0.83rem",
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DELETE MODAL
// ═══════════════════════════════════════════════════════════════════════════

export function DeleteReceiptModal({ receipt, onClose, onSuccess }) {
  const [detalles, setDetalles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/api/finance/recibo/${receipt.id}/detalles-info/`)
      .then((r) => r.json())
      .then(setDetalles)
      .catch(() => setDetalles({ tiene_detalles: false, detalles: [] }))
      .finally(() => setLoading(false));
  }, [receipt.id]);

  async function handleDelete() {
    setDeleting(true);
    setError("");
    try {
      // Depósitos sin identificar se revierten con endpoint especial
      const url = receipt.es_deposito_sin_identificar
        ? `/api/finance/recibo/${receipt.id}/revertir-deposito/`
        : `/api/finance/recibo/${receipt.id}/`;
      const method = receipt.es_deposito_sin_identificar ? 'POST' : 'DELETE';
      const res = await apiFetch(url, { method });
      if (!res.ok && res.status !== 204)
        throw new Error("Error al eliminar el recibo");
      onSuccess(receipt.es_deposito_sin_identificar
        ? "Depósito revertido correctamente."
        : "Recibo eliminado correctamente.");
      onClose();
    } catch (e) {
      setError(e.message);
      setDeleting(false);
    }
  }

  const tieneFacturas = detalles?.tiene_detalles;

  return (
    <>
      <div onClick={onClose} style={OVERLAY} />
      <div style={MODAL}>
        <ModalHeader
          icon="fa-trash"
          title={`Eliminar Recibo ${receipt.numero_recibo}`}
          subtitle={`₡${formatCurrency(receipt.monto)} · ${receipt.filial?.idFilial}`}
          onClose={onClose}
          iconColor="#f87171"
        />
        <Divider />

        {loading ? (
          <p style={{ color: "#9ca3af", textAlign: "center" }}>
            <i
              className="fa-solid fa-circle-notch fa-spin"
              style={{ marginRight: 6 }}
            />
            Verificando facturas aplicadas…
          </p>
        ) : (
          <>
            {tieneFacturas ? (
              <>
                <AlertBox type="danger">
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    <i
                      className="fa-solid fa-triangle-exclamation"
                      style={{ marginRight: 6 }}
                    />
                    Este recibo tiene {detalles.detalles.length} factura(s)
                    aplicada(s)
                  </div>
                  Al eliminarlo, las siguientes facturas volverán a quedar con
                  saldo pendiente:
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {detalles.detalles.map((d, i) => (
                      <li key={i}>
                        <strong>{d.factura}</strong> — abono de ₡
                        {formatCurrency(d.abono)}
                      </li>
                    ))}
                  </ul>
                </AlertBox>
                <AlertBox type="warning">
                  <i
                    className="fa-solid fa-circle-info"
                    style={{ marginRight: 6 }}
                  />
                  El sistema revertirá automáticamente los abonos en la cuenta
                  por cobrar de cada factura.
                </AlertBox>
              </>
            ) : (
              <AlertBox type="info">
                <i
                  className="fa-solid fa-circle-info"
                  style={{ marginRight: 6 }}
                />
                Este recibo no tiene facturas aplicadas. Se puede eliminar sin
                afectar ninguna cuenta por cobrar.
              </AlertBox>
            )}

            {error && (
              <AlertBox type="danger">
                <i
                  className="fa-solid fa-circle-exclamation"
                  style={{ marginRight: 6 }}
                />
                {error}
              </AlertBox>
            )}
          </>
        )}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid #2d3b4e",
              borderRadius: 7,
              color: "#64748b",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting || loading}
            style={{
              padding: "8px 20px",
              background: deleting ? "rgba(239,68,68,0.5)" : "#ef4444",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: deleting ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {deleting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> Eliminando…
              </>
            ) : (
              <>
                <i className="fa-solid fa-trash" />{" "}
                {tieneFacturas
                  ? "Eliminar y revertir abonos"
                  : "Eliminar recibo"}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EDIT MODAL
// ═══════════════════════════════════════════════════════════════════════════

export function EditReceiptModal({ receipt, onClose, onSuccess }) {
  const [detalles, setDetalles] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [form, setForm] = useState({
    monto: String(receipt.monto),
    fecha_pago: receipt.fecha_pago,
    metodo_pago: receipt.metodo_pago,
    documento: receipt.documento ?? "",
    descripcion: receipt.descripcion ?? "",
    cuenta_bancaria:
      receipt.cuenta_bancaria?.id ?? receipt.cuenta_bancaria ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/api/finance/recibo/${receipt.id}/detalles-info/`)
      .then((r) => r.json())
      .then(setDetalles)
      .catch(() =>
        setDetalles({
          tiene_detalles: false,
          detalles: [],
          total_aplicado: "0",
        }),
      );

    apiFetch("/api/finance/cuentaBancaria/")
      .then((r) => r.json())
      .then((d) => setCuentas(d.results ?? d))
      .catch(() => {});
  }, [receipt.id]);

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // ── Análisis del cambio de monto ──────────────────────────────────────
  const montoNuevo    = Number(form.monto) || 0;
  const montoOriginal = Number(receipt.monto);
  const aplicado      = Number(detalles?.total_aplicado ?? 0);
  const tieneFacturas = detalles?.tiene_detalles;
  const numFacturas   = detalles?.detalles?.length ?? 0;

  // CASO 2: recibo de solo aplicación de saldo a favor (monto=0)
  const esSoloSaldoFavor = montoOriginal === 0; // monto=0 = no hubo ingreso real
  // CASO 4: recibo aplicado a múltiples facturas
  const esMultiplesFacturas = numFacturas > 1;

  // Diferencia: positivo = más dinero disponible, negativo = menos
  const diferencia = montoNuevo - montoOriginal;

  // Si el nuevo monto es menor que lo ya aplicado → facturas quedan debiendo
  const quedaDebiendo = tieneFacturas && montoNuevo < aplicado;
  // Si el nuevo monto es mayor → hay excedente sin asignar
  const hayExcedente = tieneFacturas && montoNuevo > aplicado;
  // Cuánto queda sin asignar
  const excedente = tieneFacturas ? montoNuevo - aplicado : 0;

  async function handleSave() {
    if (!form.fecha_pago) {
      setError("La fecha es obligatoria.");
      return;
    }
    // Omitir validación de monto cuando está deshabilitado (Caso 2 o 4)
    if (!esSoloSaldoFavor && !esMultiplesFacturas && montoNuevo <= 0) {
      setError("El monto debe ser mayor a 0.");
      return;
    }
    // monto is disabled for Case 2/4 so it cannot change — allow saving other fields
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/finance/recibo/${receipt.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          // Solo enviar monto si no está bloqueado
          ...(!(esSoloSaldoFavor || esMultiplesFacturas) && { monto: montoNuevo }),
          fecha_pago: form.fecha_pago,
          metodo_pago: form.metodo_pago,
          documento: form.documento,
          descripcion: form.descripcion,
          cuenta_bancaria: Number(form.cuenta_bancaria),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Error al actualizar el recibo.");
      }
      onSuccess("Recibo actualizado correctamente.");
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const iStyle = { ...INPUT_STYLE };

  return (
    <>
      <div onClick={onClose} style={OVERLAY} />
      <div style={{ ...MODAL, width: "min(520px,93vw)" }}>
        <ModalHeader
          icon="fa-pencil"
          title={`Editar Recibo ${receipt.numero_recibo}`}
          subtitle={`Filial ${receipt.filial?.idFilial} · Original ₡${formatCurrency(montoOriginal)}`}
          onClose={onClose}
        />
        <Divider />

        {/* CASO 2: Solo saldo a favor */}
        {esSoloSaldoFavor && (
          <AlertBox type="danger">
            <div style={{ fontWeight:700, marginBottom:4 }}>
              <i className="fa-solid fa-ban" style={{ marginRight:6 }}/>
              Recibo de aplicación de saldo a favor — edición no permitida
            </div>
            Este recibo no ingresó dinero nuevo, solo aplicó un crédito existente.
            Para modificarlo, debe anularse y rehacerse desde Aplicar Recibo.
          </AlertBox>
        )}

        {/* CASO 4: Múltiples facturas */}
        {!esSoloSaldoFavor && esMultiplesFacturas && (
          <AlertBox type="danger">
            <div style={{ fontWeight:700, marginBottom:4 }}>
              <i className="fa-solid fa-ban" style={{ marginRight:6 }}/>
              Recibo con {numFacturas} facturas aplicadas — edición no permitida
            </div>
            Este recibo está distribuido en múltiples facturas.
            Para modificarlo, debe anularse y generarse nuevamente.
          </AlertBox>
        )}

        {/* ── Advertencias por cambio de monto ────────────────────── */}
        {tieneFacturas &&
          diferencia !== 0 &&
          (quedaDebiendo ? (
            <AlertBox type="danger">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ marginRight: 6 }}
                />
                El nuevo monto es menor que lo ya aplicado (₡
                {formatCurrency(aplicado)})
              </div>
              Con ₡{formatCurrency(montoNuevo)}, las facturas aplicadas quedarán
              con un saldo pendiente de ₡{formatCurrency(aplicado - montoNuevo)}
              . Deberás corregir los abonos manualmente en Cuentas por Cobrar.
            </AlertBox>
          ) : hayExcedente ? (
            <AlertBox type="warning">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                <i
                  className="fa-solid fa-circle-info"
                  style={{ marginRight: 6 }}
                />
                Hay ₡{formatCurrency(excedente)} sin asignar a ninguna factura
              </div>
              El excedente quedará disponible. Podés ir a{" "}
              <strong>Recibos → Aplicar</strong> para asignarlo a otras facturas
              pendientes.
            </AlertBox>
          ) : null)}

        {tieneFacturas && diferencia === 0 && (
          <AlertBox type="info">
            <i
              className="fa-solid fa-circle-check"
              style={{ marginRight: 6 }}
            />
            El monto no cambió. Las facturas aplicadas (₡
            {formatCurrency(aplicado)}) no se ven afectadas.
          </AlertBox>
        )}

        {!tieneFacturas && (
          <AlertBox type="success">
            <i
              className="fa-solid fa-circle-check"
              style={{ marginRight: 6 }}
            />
            Este recibo no tiene facturas aplicadas. Podés cambiar el monto
            libremente.
          </AlertBox>
        )}

        {/* ── Formulario ─────────────────────────────────────────── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            marginTop: 8,
          }}
        >
          <div>
            <label style={LABEL_STYLE}>
              Monto <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              style={{
                ...iStyle,
                borderColor: quedaDebiendo ? "#ef4444" : "#374151",
                opacity: (esSoloSaldoFavor || esMultiplesFacturas) ? 0.5 : 1,
                cursor: (esSoloSaldoFavor || esMultiplesFacturas) ? "not-allowed" : undefined,
              }}
              value={form.monto}
              onChange={(e) => setField("monto", e.target.value)}
              disabled={esSoloSaldoFavor || esMultiplesFacturas}
              title={esSoloSaldoFavor || esMultiplesFacturas ? "El monto no puede editarse en este recibo" : undefined}
              onFocus={(e) =>
                (e.target.style.borderColor = quedaDebiendo
                  ? "#ef4444"
                  : "#f97316")
              }
              onBlur={(e) =>
                (e.target.style.borderColor = quedaDebiendo
                  ? "#ef4444"
                  : "#374151")
              }
            />
          </div>

          <div>
            <label style={LABEL_STYLE}>
              Fecha de pago <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              type="date"
              style={iStyle}
              value={form.fecha_pago}
              onChange={(e) => setField("fecha_pago", e.target.value)}
              onFocus={(e) => (e.target.style.borderColor = "#f97316")}
              onBlur={(e) => (e.target.style.borderColor = "#374151")}
            />
          </div>

          <div>
            <label style={LABEL_STYLE}>Método de pago</label>
            <select
              style={{ ...iStyle, cursor: "pointer" }}
              value={form.metodo_pago}
              onChange={(e) => setField("metodo_pago", e.target.value)}
            >
              <option value="TRANSFERENCIA">Transferencia</option>
              <option value="EFECTIVO">Efectivo</option>
              <option value="CHEQUE">Cheque</option>
            </select>
          </div>

          <div>
            <label style={LABEL_STYLE}>Cuenta bancaria</label>
            <select
              style={{ ...iStyle, cursor: "pointer" }}
              value={form.cuenta_bancaria}
              onChange={(e) => setField("cuenta_bancaria", e.target.value)}
            >
              {cuentas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.banco} — {c.numero_cuenta}
                </option>
              ))}
            </select>
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>Referencia / Documento</label>
            <input
              type="text"
              style={iStyle}
              value={form.documento}
              onChange={(e) => setField("documento", e.target.value)}
              placeholder="N° transferencia, cheque, etc."
              onFocus={(e) => (e.target.style.borderColor = "#f97316")}
              onBlur={(e) => (e.target.style.borderColor = "#374151")}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={LABEL_STYLE}>
              Descripción <span style={{ color: "#6b7280", fontWeight: 400, fontSize: "0.75rem" }}>(opcional — anexo bancario)</span>
            </label>
            <input
              type="text"
              style={iStyle}
              value={form.descripcion}
              onChange={(e) => setField("descripcion", e.target.value)}
              placeholder="Texto para el anexo bancario"
              onFocus={(e) => (e.target.style.borderColor = "#f97316")}
              onBlur={(e) => (e.target.style.borderColor = "#374151")}
            />
          </div>
        </div>

        {/* ── Detalles de facturas aplicadas (info) ──────────────── */}
        {tieneFacturas && detalles?.detalles?.length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary
              style={{
                cursor: "pointer",
                color: "#9ca3af",
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            >
              <i className="fa-solid fa-list" style={{ marginRight: 6 }} />
              Ver {detalles.detalles.length} factura(s) aplicada(s)
            </summary>
            <div style={{ marginTop: 8, paddingLeft: 12 }}>
              {detalles.detalles.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "5px 0",
                    borderBottom: "1px solid #1f2937",
                    fontSize: "0.82rem",
                    color: "#9ca3af",
                  }}
                >
                  <span>{d.factura}</span>
                  <span style={{ color: "#4ade80", fontWeight: 600 }}>
                    ₡{formatCurrency(d.abono)} aplicados
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}

        {error && (
          <AlertBox type="danger">
            <i
              className="fa-solid fa-circle-exclamation"
              style={{ marginRight: 6 }}
            />
            {error}
          </AlertBox>
        )}

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid #2d3b4e",
              borderRadius: 7,
              color: "#64748b",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 20px",
              background: saving ? "rgba(249,115,22,0.5)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {saving ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> Guardando…
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk" /> Guardar cambios
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
